import CircleSpinner from 'components/circle-spinner/CircleSpinner'
import React, { useContext, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import ErrorBox from 'components/message-boxes/error-box/ErrorBox'
import { DocumentBox } from './DocumentBox'
import { DocumentTypes } from 'constants/enums/documentTypes'
import { IApplicationContext } from 'interfaces/i-application-context-props'
import { ApplicationDispatchContext } from 'contexts/ApplicationContext'
import pnServices from 'services/pnServices'
import { MSP_ACTIONS, MspDispatchContext } from 'components/multistep/Multistep'
import { isOnlineIdentificationStatusSigned } from 'utils/loanHelpers'
import { InfoBox } from 'components/message-boxes/information-box/InformationBox'
import { useRequiredDocuments } from 'hooks/useRequiredDocuments'
import { getDeviceData } from 'utils/commonhelpers'
import { IAcceptApplication } from 'interfaces/i-accept-application'

const REQUIRED_DOCUMENT_TYPES = [
	DocumentTypes.PromissoryNote,
	DocumentTypes.PrecontractCreditInformation,
]

export const SignPnUK = () => {
	const { t } = useTranslation()
	const { loanApplication }: IApplicationContext = useContext(
		ApplicationDispatchContext,
	)
	const applicationId = loanApplication.applicationData?.loanNumber!
	const { msp, dispatchMsp } = useContext(MspDispatchContext)

	const {
		loading: docsLoading,
		error: docsError,
		allDocumentsLoaded,
		documents,
	} = useRequiredDocuments(REQUIRED_DOCUMENT_TYPES)
	const pnEntry = documents[DocumentTypes.PromissoryNote]
	const preContractEntry =
		documents[DocumentTypes.PrecontractCreditInformation]

	const [actionLoading, setActionLoading] = useState(false)
	const [actionError, setActionError] = useState(false)
	const [terms1Approved, setTerms1Approved] = useState(false)
	const [terms2Approved, setTerms2Approved] = useState(false)
	const [terms3Approved, setTerms3Approved] = useState(false)
	const [pnDocumentReady, setPnDocumentReady] = useState(false)
	const [preContractReady, setPreContractReady] = useState(false)

	async function signPn() {
		setActionLoading(true)
		try {
			const acceptApplicationData: IAcceptApplication = {
				firstName:
					loanApplication.applicationData?.personalInfo?.firstName,
				lastName:
					loanApplication.applicationData?.personalInfo?.lastName,
				timestamp: new Date().toISOString(),
				apr: loanApplication.applicationData?.apr!,
				creditLimit: loanApplication.applicationData?.creditLimit!,
				// ssn: undefined, // we don't have ssn, and it's not mandatory in the px ep anyway, so we skip this
				// ipAddress: undefined, // this will be populated in CDA
				deviceData: getDeviceData(),
				applicationId: applicationId,
			}

			await pnServices.acceptApplication(acceptApplicationData)

			// TODO: also call cda/px with the selected marketing channel options

			const onlineIdentificationUrl =
				loanApplication.applicationData?.signingUrls
					.onlineIdentification.url
			if (onlineIdentificationUrl) {
				window.location.href = onlineIdentificationUrl
			}

			next() // if we didn't redirect to fourthline
		} catch (error) {
			console.warn(error)
			setActionError(true)
			setActionLoading(false)
		}
	}

	useEffect(() => {
		// after redirect from fourthline
		if (
			loanApplication.applicationData &&
			isOnlineIdentificationStatusSigned(loanApplication.applicationData!)
		) {
			next()
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [loanApplication.applicationData])

	const next = () => {
		dispatchMsp({
			type: MSP_ACTIONS.SET_INDEX,
			payload: {
				index: msp.index + 1,
			},
		})
	}

	if (docsLoading || actionLoading) {
		return (
			<div className="col-spinner">
				<CircleSpinner />
			</div>
		)
	}

	const errorMessage =
		docsError || actionError
			? t('ErrorMsg')
			: !allDocumentsLoaded
				? t('ErrorFetchPn')
				: undefined

	if (errorMessage) {
		return (
			<div className="error-box-container">
				<ErrorBox message={errorMessage} />
			</div>
		)
	}

	return (
		<div className="sign-pn-view-contract-section">
			<p>{t('PromissoryNoteSigningLine1')}</p>

			<div className="avarda-page-segment-white">
				<DocumentBox
					blob={pnEntry?.blob}
					document={pnEntry?.document}
					onStatusChange={setPnDocumentReady}
				/>
				<DocumentBox
					blob={preContractEntry?.blob}
					document={preContractEntry?.document}
					onStatusChange={setPreContractReady}
				/>
			</div>

			<InfoBox
				infoType="info"
				css="avarda-info"
				message={
					<>
						<strong>{t('OpenAllDocumentsHeader')}</strong>
						<br />
						{t('OpenAllDocumentsDescription')}
					</>
				}
			/>
			<h5>{t('PromissoryNoteApproveTermsHeader')}</h5>
			<div className="sign-pn-approve-checkbox-section">
				<label className="checkbox-container">
					<input
						id="sign-promissory-note-confirm-checkbox1"
						type="checkbox"
						className={`required-step ${
							terms1Approved ? 'completed-step' : ''
						}`}
						checked={terms1Approved}
						onChange={() => setTerms1Approved(!terms1Approved)}
					/>
					<span className="checkmark"></span>
					<span className="text">
						{t('PromissoryNoteApproveTerms')}
					</span>
				</label>
				<label className="checkbox-container">
					<input
						id="sign-promissory-note-confirm-checkbox2"
						type="checkbox"
						className={`required-step ${
							terms2Approved ? 'completed-step' : ''
						}`}
						checked={terms2Approved}
						onChange={() => setTerms2Approved(!terms2Approved)}
					/>
					<span className="checkmark"></span>
					<span className="text">
						{t('PromissoryNoteApproveTerms2')}
					</span>
				</label>
				<label className="checkbox-container">
					<input
						id="sign-promissory-note-confirm-checkbox3"
						type="checkbox"
						className={`required-step ${
							terms3Approved ? 'completed-step' : ''
						}`}
						checked={terms3Approved}
						onChange={() => setTerms3Approved(!terms3Approved)}
					/>
					<span className="checkmark"></span>
					<span className="text">
						{t('PromissoryNoteApproveTerms3')}
					</span>
				</label>
			</div>

			<InfoBox
				infoType="warning"
				css="avarda-warning"
				message={
					<>
						<strong>{t('CreditCardInfoHeader')}</strong>
						<br />
						{t('CreditCardInfo')}
					</>
				}
			/>
			<br />
			<div className="sign-pn-button-section step-actions">
				<button
					id="sign-promissory-note-sign-contract-button"
					type="button"
					className="btn btn-primary"
					disabled={
						!terms1Approved ||
						!terms2Approved ||
						!terms3Approved ||
						!pnDocumentReady ||
						!preContractReady
					}
					onClick={() => {
						setTimeout(() => {
							signPn()
						}, 400)
					}}>
					{t('SIGNPN')}
				</button>
			</div>
		</div>
	)
}
